import { Apple, Smartphone, Share, MoreVertical, Plus, Download, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { usePWA } from "@/hooks/usePWA";
import { AppStoreCTA } from "@/components/AppStoreCTA";

const Install = () => {
  const navigate = useNavigate();
  const { isInstalled, isInstallable, promptInstall } = usePWA();

  const iosSteps = [
    {
      step: 1,
      title: "Open in Safari",
      description: "Make sure you're viewing this page in Safari browser",
      icon: <Apple className="h-5 w-5" />,
    },
    {
      step: 2,
      title: "Tap the Share button",
      description: "Look for the share icon at the bottom of your screen",
      icon: <Share className="h-5 w-5" />,
    },
    {
      step: 3,
      title: "Scroll and tap 'Add to Home Screen'",
      description: "You may need to scroll down in the share menu to find this option",
      icon: <Plus className="h-5 w-5" />,
    },
    {
      step: 4,
      title: "Tap 'Add'",
      description: "Confirm by tapping Add in the top right corner",
      icon: <Download className="h-5 w-5" />,
    },
  ];

  const androidSteps = [
    {
      step: 1,
      title: "Open in Chrome",
      description: "Make sure you're viewing this page in Chrome browser",
      icon: <Smartphone className="h-5 w-5" />,
    },
    {
      step: 2,
      title: "Tap the menu button",
      description: "Look for the three dots (⋮) in the top right corner",
      icon: <MoreVertical className="h-5 w-5" />,
    },
    {
      step: 3,
      title: "Tap 'Install app' or 'Add to Home screen'",
      description: "Select this option from the menu",
      icon: <Download className="h-5 w-5" />,
    },
    {
      step: 4,
      title: "Confirm installation",
      description: "Tap 'Install' in the popup dialog",
      icon: <Plus className="h-5 w-5" />,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold">Install App</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Primary path: the real iOS app */}
        <div className="mb-8">
          <AppStoreCTA
            placement="install_page"
            headline="Get Airport Buddy on the App Store"
            subline="The iPhone app is the fastest way in — push alerts, saved airports, and a leave-by timer. Prefer the web version? Follow the steps below."
          />
        </div>

        {isInstalled ? (
          <Card className="mb-8 border-green-500/50 bg-green-500/10">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2">App Already Installed!</h2>
              <p className="text-muted-foreground">
                You're all set. The app is installed on your device.
              </p>
            </CardContent>
          </Card>
        ) : isInstallable ? (
          <Card className="mb-8 border-primary/50 bg-primary/5">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Quick Install Available</h2>
              <p className="text-muted-foreground mb-4">
                Your browser supports one-click installation!
              </p>
              <Button onClick={promptInstall} size="lg">
                <Download className="h-4 w-4 mr-2" />
                Install Now
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-8">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Manual Installation Required</h2>
              <p className="text-muted-foreground text-sm">
                Your browser requires manual installation. Follow the steps below for your device.
                <span className="block mt-2 text-xs">
                  (iOS/Safari always requires manual installation—this is normal!)
                </span>
              </p>
            </CardContent>
          </Card>
        )}

        <div className="space-y-8">
          {/* iOS Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center">
                  <Apple className="h-5 w-5" />
                </div>
                iPhone / iPad (iOS)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {iosSteps.map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center font-semibold text-sm">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-muted-foreground">{item.icon}</span>
                        <h3 className="font-medium">{item.title}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Android Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center">
                  <Smartphone className="h-5 w-5" />
                </div>
                Android
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {androidSteps.map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center font-semibold text-sm">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-muted-foreground">{item.icon}</span>
                        <h3 className="font-medium">{item.title}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Benefits */}
          <Card>
            <CardHeader>
              <CardTitle>Why Install?</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
                  <span>Quick access from your home screen</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
                  <span>Works offline with cached data</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
                  <span>Full-screen experience without browser UI</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
                  <span>Faster loading times after installation</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Install;
